import { SocialMediaAccountPublic } from '@/lib/api';
import { PlatformBadge } from './PlatformBadge';
import { AlertCircle, CheckCircle2, Circle } from 'lucide-react';

interface PlatformSelectorProps {
  accounts: SocialMediaAccountPublic[];
  selectedAccountIds: string[];
  onToggle: (accountId: string) => void;
  /** @deprecated No longer used in composer. Kept for backward compatibility. */
  onDisconnect?: (accountId: string) => void;
}

export function PlatformSelector({
  accounts,
  selectedAccountIds,
  onToggle,
}: PlatformSelectorProps) {
  if (accounts.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No connected accounts. Go to Accounts to connect your social media profiles.
      </p>
    );
  }

  return (
    <div className="flex flex-wrap gap-3">
      {accounts.map((account) => {
        const isSelected = selectedAccountIds.includes(account.accountId);
        const isActive = account.status === 'active';

        return (
          <button
            key={account.accountId}
            type="button"
            onClick={() => isActive && onToggle(account.accountId)}
            disabled={!isActive}
            className={[
              'flex items-center gap-2.5 rounded-lg border p-3 text-left transition-all',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              isActive ? 'cursor-pointer hover:bg-muted/50' : 'cursor-not-allowed opacity-50',
              isSelected ? 'border-primary bg-primary/5 ring-1 ring-primary' : 'bg-card',
            ].join(' ')}
            aria-pressed={isSelected}
            title={!isActive ? (account.lastError ?? 'Account inactive') : undefined}
          >
            {account.platformAvatarUrl ? (
              <img
                src={account.platformAvatarUrl}
                alt=""
                className="h-8 w-8 rounded-full flex-shrink-0"
              />
            ) : (
              <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center text-muted-foreground text-sm font-bold flex-shrink-0">
                {(account.platformName ?? account.platform)[0].toUpperCase()}
              </div>
            )}

            <div className="min-w-0">
              <div className="flex items-center gap-1.5">
                <PlatformBadge platform={account.platform} size="sm" />
                {!isActive && <AlertCircle className="h-3 w-3 text-destructive flex-shrink-0" />}
              </div>
              {account.platformUsername && (
                <p className="text-xs text-muted-foreground truncate mt-0.5">
                  @{account.platformUsername}
                </p>
              )}
            </div>

            {isActive &&
              (isSelected ? (
                <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0 ml-1" />
              ) : (
                <Circle className="h-4 w-4 text-muted-foreground flex-shrink-0 ml-1" />
              ))}
          </button>
        );
      })}
    </div>
  );
}
